import { Outlet } from 'react-router';

import { useSession } from '@/features/auth/useSession';
import { type Capability, capabilitiesOf, hasAny } from '@/shared/session/profiles';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Spinner } from '@/shared/ui/Spinner';

/**
 * Guard for a screen that belongs to some profiles (contract v10.47, ADR 0042 of the
 * back): Presupuestos for «Dirección y compras» and, from the tariffs, the shop (any of
 * `quotes` and `tariff_quotes`, contract v10.52), Clientes for both. It hangs inside
 * `RequireAuth`, so there is a session; what it adds is the capability.
 *
 * **A convenience, not the control**: the API answers 403 with `X-Cubas-Reason: profile`
 * to the whole area whatever this does. The refusal says who to ask and nothing about
 * prices: the screen of a salesperson may be in front of a customer.
 */
export function RequireCapability({
  capability,
}: {
  capability: Capability | readonly Capability[];
}) {
  const session = useSession();

  if (session.isPending) {
    return <Spinner variant="fill" label="Comprobando permisos" />;
  }

  if (!hasAny(capabilitiesOf(session.data), capability)) {
    return (
      <EmptyState
        title="Tu perfil no incluye esta pantalla"
        hint="Si la necesitas, pídesela a quien administra la aplicación."
      />
    );
  }

  return <Outlet />;
}
